import { Turnstile } from '@marsidev/react-turnstile';
import React, { useEffect, useState } from 'react';
import { FiAlertCircle, FiMail } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import axios from '../../utils/axiosConfig';
import ExternalLink from '../common/ExternalLink';

const OpenRegistration = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: verify, 3: complete
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form data
  const [email, setEmail] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  // Turnstile
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileConfig, setTurnstileConfig] = useState(null);

  // Fetch Turnstile configuration on mount
  useEffect(() => {
    fetchTurnstileConfig();

    // Check if Turnstile script is loaded
    if (window.turnstile) {
      console.log('Turnstile script already loaded');
    } else {
      console.log('Turnstile script not yet loaded');
    }
  }, []);

  const fetchTurnstileConfig = async () => {
    try {
      const response = await axios.get('/api/v1/config/turnstile');
      setTurnstileConfig(response.data);
    } catch (error) {
      console.error('Failed to load Turnstile config:', error);
    }
  };

  // Step 1: Initiate registration with email
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate .edu email requirement
    if (!email.toLowerCase().endsWith('.edu')) {
      setError(
        'Free trial registration requires a .edu email address. Please use your university email.'
      );
      return;
    }

    // Check Terms agreement
    if (!agreeToTerms) {
      setError('Please agree to the Terms of Service and Privacy Policy');
      return;
    }

    console.log('Submitting with:', {
      turnstileToken,
      turnstileConfig,
      enabled: turnstileConfig?.enabled,
    });

    if (!turnstileToken && turnstileConfig?.enabled && !turnstileConfig?.testMode) {
      setError('Please complete the security verification');
      return;
    }

    // In test mode, use a dummy token if none provided
    const tokenToSend = turnstileToken || (turnstileConfig?.testMode ? 'test-token' : null);

    setLoading(true);

    try {
      const response = await axios.post('/api/v1/open/register/init', {
        email,
        turnstile_token: tokenToSend,
      });

      setSuccess(response.data.message);
      setStep(2); // Move to verification step

      // Store trial info to display
      if (response.data.trial_info) {
        sessionStorage.setItem('trial_info', JSON.stringify(response.data.trial_info));
      }
    } catch (error) {
      // Handle structured error responses from rate limiter
      let errorMessage = 'Registration failed. Please try again.';

      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;

        // If detail is an object (rate limit response), extract the message
        if (typeof detail === 'object' && detail.message) {
          errorMessage = detail.message;
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }

      setError(errorMessage);
      // Reset Turnstile on error
      if (window.turnstile && turnstileConfig?.enabled) {
        window.turnstile.reset();
        setTurnstileToken('');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-lg">
        <div className="flex justify-center">
          <img
            src={process.env.PUBLIC_URL + '/maincar.png'}
            alt="InternHub Logo"
            className="h-14 mb-2"
          />
          <span className="text-blue-600 text-3xl font-bold ml-2 self-center">InternHub</span>
        </div>
        <h2 className="mt-4 text-center text-3xl font-extrabold text-gray-900">
          Sign up for your 2 month free trial!
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-xl">
        <div className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10">
          {/* Progress Steps */}
          {/* <div className="flex items-center justify-center mb-6">
                        <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${step >= 1 ? 'border-blue-600 bg-blue-50' : 'border-gray-300'
                                }`}>
                                1
                            </div>
                            <span className="ml-2 text-sm font-medium">Enter Email</span>
                        </div>
                        <div className={`flex-1 h-0.5 mx-4 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-300'}`} />
                        <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${step >= 2 ? 'border-blue-600 bg-blue-50' : 'border-gray-300'
                                }`}>
                                2
                            </div>
                            <span className="ml-2 text-sm font-medium">Check Email</span>
                        </div>
                    </div> */}

          {/* Error/Success Messages */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
              <FiAlertCircle className="text-red-500 mt-0.5 mr-2 shrink-0" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {/* Step 1: Email Registration */}
          {step === 1 && (
            <form onSubmit={handleEmailSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  University Email Address (.edu required)
                </label>

                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-xs placeholder-gray-400 focus:outline-hidden focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="your.name@university.edu"
                  />
                </div>
                <p className="text-xs text-gray-500 my-2">
                  Free trial requires a .edu email. Having trouble? Email support@internhubapp.com
                </p>
              </div>

              {/* Terms and Privacy Policy Agreement */}
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="agree-terms"
                    name="agree-terms"
                    type="checkbox"
                    checked={agreeToTerms}
                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                    className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded-sm"
                  />
                </div>
                <div className="ml-2 text-sm">
                  <label htmlFor="agree-terms" className="text-gray-600">
                    I agree to the{' '}
                    <ExternalLink
                      href="/our-terms-of-service"
                      className="font-medium text-blue-600 hover:text-blue-500"
                    >
                      Terms of Service
                    </ExternalLink>{' '}
                    and{' '}
                    <ExternalLink
                      href="/our-privacy-policy"
                      className="font-medium text-blue-600 hover:text-blue-500"
                    >
                      Privacy Policy
                    </ExternalLink>
                  </label>
                </div>
              </div>

              {/* Turnstile Widget */}
              {turnstileConfig?.enabled && turnstileConfig?.siteKey && (
                <div className="w-full">
                  {turnstileConfig.testMode && (
                    <div className="text-xs text-amber-600 mb-2 text-center">
                      🧪 Test Mode - Verification will always pass
                    </div>
                  )}
                  <div className="flex justify-center">
                    <Turnstile
                      siteKey={turnstileConfig.siteKey}
                      onSuccess={(token) => {
                        console.log('Turnstile verified:', token);
                        setTurnstileToken(token);
                      }}
                      onError={(error) => {
                        console.error('Turnstile error:', error);
                        setError('Verification failed. Please refresh and try again.');
                        setTurnstileToken('');
                      }}
                      onExpire={() => {
                        console.log('Turnstile token expired');
                        setTurnstileToken('');
                      }}
                      options={{
                        theme: 'light',
                        size: 'flexible',
                      }}
                    />
                  </div>
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={loading || !agreeToTerms}
                  className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-xs text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${loading || !agreeToTerms ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {loading ? 'Sending verification...' : 'Sign up!'}
                </button>
              </div>
            </form>
          )}

          {/* Step 2: Email Verification */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="mb-4">
                  <FiMail className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                  {/* <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                        Check Your Email
                                    </h3> */}
                  <p className="text-gray-600 text-sm">We've sent a verification link to:</p>
                  <p className="font-medium text-gray-900 mt-1">{email}</p>
                </div>
              </div>

              {/* <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                                <p className="text-sm text-blue-800">
                                    Click the link in your email to verify your account and continue registration.
                                    The link will expire in 24 hours.
                                </p>
                            </div> */}

              <div>
                <p className="text-sm text-gray-500 mb-4 text-center">
                  Didn't receive the email? Check your spam folder or{' '}
                  <button
                    onClick={() => setStep(1)}
                    className="font-medium text-blue-600 hover:text-blue-500"
                  >
                    start over
                  </button>
                </p>
              </div>
            </div>
          )}

          {/* Footer Links */}
          {/* <div className="mt-6 text-center">
                        <p className="text-sm text-gray-600">
                            Already have an account?{' '}
                            <button
                                onClick={() => navigate('/login')}
                                className="text-blue-600 hover:text-blue-500 font-medium"
                            >
                                Sign in
                            </button>
                        </p>
                    </div> */}
        </div>
      </div>
    </div>
  );
};

export default OpenRegistration;
